import * as React from 'react'
import cx from 'classnames'
import Input from '@mui/material/Input'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { MenuProps } from '@mui/material/Menu'
import Chip from '@mui/material/Chip'
import * as styles from './multiple.select.chip.styl'
import { find, isArray } from 'lodash'
import { orderByField } from '@src/utils/array.utils'

export type SelectValue = string | number | string[]
export interface SelectOption<
  V extends SelectValue,
  L extends string = string
> {
  value: V
  label: L
  disabled?: boolean
}

export interface MultipleSelectProps<T extends SelectValue> {
  role?: string
  displayEmpty?: boolean
  placeholder?: string
  className?: string
  classNameItem?: string
  onChange: (value: T[], event: SelectChangeEvent<T[]>) => void
  value: T[]
  multiple?: boolean
  options: SelectOption<T, string>[]
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuPropsOptions: Partial<MenuProps> = {
  disablePortal: true,
  variant: 'menu',
  autoFocus: false,
  PaperProps: {
    style: {
      color: '#666',
      fontSize: '16px',
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const MultipleSelect = <T extends SelectValue>(
  props: MultipleSelectProps<T>
) => {
  const {
    onChange,
    multiple,
    placeholder,
    options,
    role,
    className,
    classNameItem,
    displayEmpty,
    value
  } = props
  function getValue<B extends boolean, T>(multiple: B, value: T | T[]): T[] {
    return multiple ? (value as T[]) : ([value] as T[])
  }

  function getLabel(options: SelectOption<T, string>[], value: any) {
    return find(options, x => x?.value === value)?.label || value
  }

  function handleChange(event: SelectChangeEvent<T[]>, child: React.ReactNode) {
    const { target } = event
    const { value = [] } = target
    if (typeof onChange === 'function') {
      onChange(getValue(multiple, value as T[]), event)
    }
  }

  function renderValue(selected) {
    let selectedValues = []
    if (isArray(selected)) {
      selectedValues = selected
    } else {
      selectedValues = multiple ? selected : [selected]
    }

    return (
      <div className={styles.chips}>
        {selectedValues && selectedValues.length > 0 ? (
          selectedValues.map((value, index) => (
            <Chip
              key={index}
              label={getLabel(options, value)}
              className={styles.chip}
            />
          ))
        ) : (
          <span role="placeholder">{placeholder}</span>
        )}
      </div>
    )
  }

  return (
    <div className={cx(className, styles.selectWrapper)}>
      <Select
        role={role}
        multiple={multiple}
        displayEmpty={displayEmpty}
        value={value}
        onChange={handleChange}
        input={<Input className={styles.input} />}
        renderValue={renderValue}
        MenuProps={MenuPropsOptions}
      >
        {orderByField({ options, key: 'label' }).map((option, index) => (
          <MenuItem
            disabled={option?.disabled}
            data-index={index}
            className={cx(styles.menuItem, classNameItem)}
            key={index}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </div>
  )
}

MultipleSelect.defaultProps = {
  displayEmpty: false,
  placeholder: 'Select...',
  options: [],
  value: []
}

export default React.memo(MultipleSelect)
